<template>
  <base-modal
    name="adminVideoBearbeiten"
    :title="$t('admin.videos.edit_title')"
    @submit="saveVideoChange($event, get('video').id)"
  >
    <!-- Titel des Videos -->
    <div class="text-body-2 font-weight-medium mb-2">{{ $t('modal.video_edit.title_label') }}</div>
    <v-text-field
      name="title"
      v-model="get('video').title"
      :placeholder="$t('modal.video_edit.title_placeholder')"
      :rules="videoTitleRules"
      counter="52"
      outlined
      dense
      required
      hide-details="auto"
    />

    <!-- Beschreibung -->
    <div class="text-body-2 font-weight-medium mb-2 mt-4">{{ $t('shop.admin_video_edit.description_label') }}</div>
    <v-textarea
      name="description"
      v-model="get('video').description"
      :rules="videoDescriptionRules"
      counter="2000"
      outlined
      dense
      hide-details="auto"
    ></v-textarea>

    <!-- Kategorie -->
    <div class="text-body-2 font-weight-medium mb-2 mt-4">{{ $t('modal.video_edit.categories_label') }}</div>
    <v-select
      v-if="get('video').categorie != null"
      name="categories"
      v-model="get('video').categorie.id"
      :items="get('categories')"
      item-text="name"
      item-value="id"
      outlined
      dense
      hide-details="auto"
    ></v-select>
    <v-select
      v-else
      name="categories"
      :rules="[(v) => !!v || $t('modal.video_edit.categories_required')]"
      v-model="categories"
      :items="get('categories')"
      item-text="name"
      item-value="id"
      outlined
      dense
      hide-details="auto"
    ></v-select>

    <!-- Altersfreigabe -->
    <div class="text-body-2 font-weight-medium mb-2 mt-4">{{ $t('shop.admin_video_edit.age_rating_label') }}</div>
    <v-select
      name="fsk"
      v-model="get('video').fsk"
      :items="fskItems"
      item-text="fsk"
      item-value="id"
      outlined
      dense
      hide-details="auto"
    ></v-select>

    <v-divider class="mt-6 mb-4"></v-divider>

    <!-- Thumbnail -->
    <div class="text-h6 font-weight-bold my-2">{{ $t('shop.admin_video_edit.thumbnail_label') }}</div>
    <div class="text-body-2 font-weight-medium mb-2">{{ $t('modal.video_edit.thumbnail_upload_label') }}</div>
    <VueFileAgent
      ref="image"
      :deletable="true"
      :meta="true"
      :accept="'image/*'"
      :maxSize="'30MB'"
      :maxFiles="1"
      :errorText="{
        type: $t('shop.admin_video_edit.thumbnail_error_type'),
        size: $t('modal.video_edit.thumbnail_error_size'),
      }"
      @beforedelete="onBeforeDeleteImage($event)"
      @delete="imageDeleted($event)"
      @change="thumbnailUpload(get('video').id)"
      v-model="image"
    >
      <template v-slot:file-preview-new>
        <div
          key="new"
          class="file-preview-wrapper grid-box-item grid-block file-preview-new"
        >
          <span class="file-preview">
            <div
              style="
                position: absolute;
                top: 15px;
                right: 0px;
                bottom: 0px;
                left: 0px;
              "
            >
              <v-icon size="16px">mdi-image</v-icon>
              <span class="help-text" style="color: '#00C3FF'"
                >{{ $t('modal.video_edit.dropzone_hint') }}</span
              >
            </div>
          </span>
        </div>
      </template>
    </VueFileAgent>
    <v-divider class="my-4"></v-divider>

    <!-- Sichtbarkeit -->
    <div class="text-h6 font-weight-bold my-2">{{ $t('shop.admin_video_edit.visibility_label') }}</div>

    <!-- On Stage? -->
    <div class="d-flex justify-space-between align-center mt-2">
      <div>
        <span class="body-1">{{ $t('modal.video_edit.on_stage_label') }}</span>
        <span
          class="fas fa-check green--text pl-4"
          v-if="get('video').published == true"
        ></span>
        <p class="caption grey--text ma-0">
          {{ $t('modal.video_edit.on_stage_hint') }}
        </p>
      </div>
      <div class="d-flex align-center">
        <span class="caption grey--text mr-2">{{ $t('common.no') }} / {{ $t('common.yes') }}</span>
        <v-switch
          name="published"
          v-model="get('video').published"
          dense
          hide-details
          class="mt-0"
        ></v-switch>
      </div>
    </div>

    <!-- Veröffentlichen? -->
    <div class="d-flex justify-space-between align-center mt-4">
      <div>
        <span class="body-1">{{ $t('modal.video_edit.publish_label') }}</span>
        <span
          class="fas fa-check green--text pl-4"
          v-if="get('video').share == true"
        ></span>
        <p class="caption grey--text ma-0">
          {{ $t('modal.video_edit.publish_hint_line1') }}<br />{{
            $t('modal.video_edit.publish_hint_line2')
          }}
        </p>
      </div>
      <div class="d-flex align-center">
        <span class="caption grey--text mr-2">{{ $t('common.no') }} / {{ $t('common.yes') }}</span>
        <v-switch
          v-model="get('video').share"
          name="share"
          dense
          hide-details
          class="mt-0"
        />
      </div>
    </div>

    <div class="mt-4" v-if="get('video').share == true">
      <div>
        <p class="font-weight-bold">
          {{ $t('modal.video_edit.embed_snippet_label') }}
        </p>
        <div class="d-flex justify-space-between">
          <code id="shareItem" class="mx-2 pa-4">{{
            shareItem(get("video"))
          }}</code>
          <v-btn
            @click="copyCodeSnippet(shareItem(get('video')))"
            small
            color="lightblue"
          >
            <v-icon size="16" class="mr-2">mdi-content-copy</v-icon>{{ $t('shop.admin_video_edit.copy') }}
          </v-btn>
        </div>
      </div>

      <div class="mt-4">
        <p class="font-weight-bold">{{ $t('shop.admin_video_edit.share_link_label') }}</p>
        <div class="d-flex mt-4 justify-space-between">
          <p class="">{{ shareLink(get("video")) }}</p>

          <v-btn
            @click="copyCodeSnippet(shareLink(get('video')))"
            small
            color="lightblue"
          >
            <v-icon size="16" class="mr-2">mdi-content-copy</v-icon>{{ $t('shop.admin_video_edit.copy') }}
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Kommentare freischalten? -->
    <div class="d-flex justify-space-between align-center mt-4">
      <div>
        <span class="body-1">{{ $t('modal.video_edit.comments_label') }}</span>
        <span
          class="fas fa-check green--text pl-4"
          v-if="get('video').comment == true"
        ></span>
        <p class="caption grey--text ma-0">
          {{ $t('modal.video_edit.comments_hint') }}
        </p>
      </div>
      <div class="d-flex align-center">
        <span class="caption grey--text mr-2">{{ $t('common.no') }} / {{ $t('common.yes') }}</span>
        <v-switch
          name="comment"
          v-model="get('video').comment"
          dense
          hide-details
          class="mt-0"
        ></v-switch>
      </div>
    </div>
  </base-modal>
</template>

<script>
// import Modal from "./modal"

import APIService from "@/services/api.service";
import { mapGetters } from "vuex";

export default {
  name: "adminVideoBearbeiten",
  components: {
    BaseModal: () => import("./baseModal"),
  },
  data() {
    return {
      title: "",
      hours: "",
      minutes: "",
      seconds: "",
      share: "",
      WhatsApp: "",
      published: "",
      categorie: null,
      categories: [],
      image: [],
      fileRecordsForUpload: [],
      fileRecords: [],
      fskItems: [
        { id: 0, fsk: "Ohne Altersbeschränkung" },
        { id: 6, fsk: "Ab 6 Jahren" },
        { id: 12, fsk: "Ab 12 Jahren" },
        { id: 16, fsk: "Ab 16 Jahren" },
        { id: 18, fsk: "Ab 18 Jahren" },
      ],
      valid: false,
      videoTitleRules: [
        (v) => !!v || this.$t('modal.video_edit.title_required'),
        (v) =>
          (v && v.length <= 52) ||
          this.$t('modal.video_edit.title_max_length'),
        (v) =>
          (v && v.length >= 2) ||
          this.$t('modal.video_edit.title_min_length'),
      ],
      videoDescriptionRules: [
        (v) => !!v || this.$t('modal.video_edit.description_required'),
        (v) =>
          (v && v.length <= 2000) ||
          this.$t('modal.video_edit.description_max_length'),
        (v) =>
          (v && v.length >= 2) ||
          this.$t('modal.video_edit.description_min_length'),
      ],
    };
  },

  computed: {
    ...mapGetters(["get"]),
  },

  watch: {
    videoCategories: {
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.selectedCategories = newVal.map(cat => cat.id);
          this.initialCategories = newVal.map(cat => cat.id);
        } else {
          this.selectedCategories = [];
          this.initialCategories = [];
        }
      },
      immediate: true
    },
  },
  methods: {
    thumbnailUpload(id) {
      setTimeout(() => {
        this.$store
          .dispatch("uploadFiles", {
            endpoint: `/api/v1/videos/${id}/upload`,
            files: { image: this.image },
            metadata: { width: 100, height: 100 },
            message: false,
          })
          .then((response) => {
            this.resetForm();
            APIService.setResponse({
              message: this.$t('shop.admin_video_edit.response.saved'),
              color: "blue",
            });
          })
          .catch((error) => {
            APIService.setResponse({ message: ``, color: "red" });
          });
      }, 100);
    },

    shareItem(item, style = 'height="500px" width="100%') {
      const src = `${window.location.protocol}//${window.location.host}/videos/${item.id}`;
      let iframe = "";
      if (item.share) {
        iframe = `<iframe ${style}" src="${src}" title="${this.$store.state.main.config.result.site_name}"></iframe>`;
      }
      return iframe;
    },

    shareLink(item) {
      return `${window.location.protocol}//${window.location.host}/videos/${item.id}`;
    },

    publishVideo(id) {
      var published = this.$store.state.main.video.result.share;
      published != published;
      this.$store.dispatch("patch", {
        endpoint: `/api/v1/videos/${id}`,
        event: { share: published },
        message: false,
      });
    },

    stageVideo(id) {
      var staged = this.$store.state.main.video.result.published;
      staged != staged;
      this.$store.dispatch("patch", {
        endpoint: `/api/v1/videos/${id}`,
        event: { published: staged },
        message: false,
      });
    },

    copyCodeSnippet(item) {
      // var copyText = this.shareItem(this.$store.state.main.video.result)
      navigator.clipboard.writeText(item);
      APIService.setResponse({ message: this.$t('shop.admin_video_edit.response.copied'), color: "blue" });
    },

    close(name) {
      this.$store.dispatch("modal/close", name);
    },

    resetForm() {
      this.image = [];
      this.video = [];
      this.fileRecordsForUpload = [];
    },

    deleteVideoFromCategory(item) {
      const index = this.selectedCategories.indexOf(item.id);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      }
    },

    saveVideoChange(e, id) {
      const object = APIService.getFormData(APIService.getForm(e));
      object.fsk = parseInt(object.fsk);

      this.$store
        .dispatch("patch", {
          endpoint: `/api/v1/videos/${id}`,
          event: object,
          state: "video",
          message: false,
        })
        .then(() => {
          this.$store.dispatch("fetch", {
            endpoint: "/api/v1/videos?sort=DESC",
            state: "videos",
          });
          this.$store.dispatch("fetch", {
            endpoint: "/api/v1/categories",
            state: "categories",
          });
          this.$store.dispatch("modal/close", "adminVideoBearbeiten");
          APIService.setResponse({
            message: this.$t('shop.admin_video_edit.response.save_success'),
            color: "green",
          });
        })
        .catch((error) => {
          APIService.setResponse({
            message: this.$t('modal.video_edit.save_error_title'),
            color: "red",
          });
        });
    },
  },
  created() {
    this.$store.dispatch("fetch", {
      endpoint: "/api/v1/categories",
      state: "categories",
    });
  },
};
</script>


<style lang="sass">
#shareItem code
	padding: 20px
	background-color: var(lightgrey)
	font-size: 12pt
	overflow: auto
	white-space: pre
</style>
